import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { Pagination } from '@gerdau/hefesto-mais-web';
import { Button } from '@gerdau/hefesto';

import CartProductTable, { OptionsProps } from './CartProductTable';
import CartHeader, { CartHeaderGraph } from './CartHeader';
import {
  CartBpInfo,
  PaginationContainer,
  ShoppingCartContainer
} from './style';
import SelectedBpCard from '../../../../components/SelectedBpCard';
import { IShipToCustomerItem } from '../../../../services/EntryOrder';
import {
  CartOptions,
  ChangePaymentMethodModalProps,
  ErrorProps,
  ItemTableProps,
  MeasureUnit,
  MinimumOrderItemProps,
  NewCartItem,
  NewCartOption,
  PaymentTerm,
  SlicePaginationProps,
  UpdateItemProps,
  ValidationModalProps
} from '../../../../services/MVP/models';
import { SummaryBottom } from './SummaryBottom';
import { EMeasureUnit } from '../../../../store/commomTypes';
import { useCart } from './useCart';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { ApplicationState } from '../../../../store';
import MessageChooseItem from '../../../../components/MessageChooseItem';
import { useHistory, useLocation } from 'react-router-dom';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { hasDuplicates, hasCommonPaymentType } from './validate';
import { ValidationModal } from './ValidationModal';
import { ERoutes } from '../../../../routes';
import moment from 'moment';
import { orderSimulateRequestSuccess } from '../../../../store/ducks/order-simulate-checkout-mvp/actions';
import {
  ICheckoutResponse,
  IDataOrderSimulate
} from '../../../../store/ducks/order-simulate-checkout-mvp/types';
import { useCartService } from '../../../../services/MVP/useCartService';
import { useBulkErrorToast } from '../../../../services/MVP/useBulkErrorToast';
import { CheckoutServiceMvp } from '../../../../services/CheckoutMvp';
import { EPurchaseRoutes } from '../../purchase.routes';
import { ICustomer } from '../../../../store/ducks/customers/types';
import { BreadcrumbItemProps } from '@gerdau/hefesto-mais-web/Breadcrumbs/Breadcrumbs.types';
import { ELocalStorageKey } from '../../../../services/EGerdauService/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../store/ducks/tagging/types';
import {
  parseNumber,
  paymentAnticipated,
  purchaseOrigin
} from '../../../../services/MVP/utils';
import {
  getTipoCompraForItemId,
  getLastTipoCompra,
  migrateTipoCompraFromSkuToItemId
} from '../../../../services/MVP/tipoCompraMap';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { normalizeQuantity } from '../../../../utils/purchaseHelpers';
import { GetFlag } from '../../../../utils/featureFlag';
import {
  FEATURE_FLAG_CROSS_SELL,
  FEATURE_FLAG_CROSS_SELL_DYNAMIC
} from '../../../../utils/cross-sell';
import { useShoppingCart } from '../../../../providers/shoppingcart.provider';
import { EOrderSettingsRoutes } from '../OrderSettingsMvp/orderSettings.routes';
import { useLoadingModal } from '../../../../context/useModaContext';
import LoadingModal from '../../../../components/LoadingModal';
import {
  checkCrossSellAvailable,
  cleanCrossSellItems
} from '../../../../store/ducks/cross-sell/actions';
import { useApiInstance } from '../../../../services/MVP/useApiInstance';
import { ChangePaymentMethodModal } from './ChangePaymentMethodModal';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { useCheckout } from '../../../../providers/checkout.provider';
import { CHECKOUT_ORDERS_INITIAL_STATE } from '../../../../store/ducks/order-simulate-checkout-mvp';
import { useCartWishlist } from '../../../../hooks/useCartWishlist';

const sliceItems = ({ cartItems, page, size }: SlicePaginationProps) => {
  return cartItems.slice((page - 1) * size, page * size);
};

const formatCartItems = (items: NewCartItem[]): NewCartItem[] => {
  return items.map((productItem) => {
    const productImage = productItem.extension_attributes.product_image || '';
    const imageFile = productImage.includes('/media/')
      ? productImage.slice(productImage.indexOf('/media/'))
      : productImage;

    return {
      ...productItem,
      media_gallery_entries: [
        {
          id: productItem.item_id,
          media_type: 'image',
          label: null,
          position: 1,
          disabled: false,
          types: ['image', 'small_image', 'thumbnail', 'swatch_image'],
          file: imageFile
        }
      ],
      sap_descricao_gpm: productItem.extension_attributes?.gpm || '',
      descricao_gpm: productItem.extension_attributes?.description_gpm || ''
    };
  });
};

const CartPagination = ({
  handleChange,
  total,
  loading
}: {
  handleChange: (page: number, size: number) => void;
  total: number;
  loading: boolean;
}) => {
  if (loading && total === 0) {
    return null;
  }
  return (
    <PaginationContainer disableNextPage={total <= 12}>
      <Pagination
        ariaLabel="pagination"
        onChange={(page: number, pageTotal: number) =>
          total <= 12 ? null : handleChange(page, pageTotal)
        }
        total={total}
        showTotal
        pageSizeOptions={[12, 24, 30]}
      />
    </PaginationContainer>
  );
};

const ShoppingCartMvp = ({ crossSellKey }: { crossSellKey: string }) => {
  const { pathname, state } = useLocation<{ isRepeatOrder?: boolean }>();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);
  const history = useHistory();
  const tipo_compra = getLastTipoCompra(purchaseOrigin());
  const {
    cartId,
    cartData,
    getProductsInformation,
    addBulkGetCart,
    deleteAllCartItems
  } = useCart();
  const { deleteItem, deleteBulkItems, updateCartItem } = useCartService();
  const { dispatchBulkErrorToast } = useBulkErrorToast();
  const { dirtyCart, updateDirtyCartState } = useShoppingCart();
  const { updateCheckoutData } = useCheckout();
  const shoppingCartRef = useRef<HTMLDivElement>(null);
  const cartInitializedRef = useRef(false);
  const [goToCheckout, setGoToCheckout] = useState(false);
  const [cartItems, setCartItems] = useState<NewCartItem[]>([]);
  const [dirtyCartItems, setDirtyCartItems] = useState<NewCartItem[]>([]);
  const [minimunOrder, setMinimunOrder] = useState<MinimumOrderItemProps[]>([]);

  const newCartEndpoint = GetFlag('new_cart_endpoints');

  const crossSellFeatureFlag = GetFlag(FEATURE_FLAG_CROSS_SELL);
  const crossSellNewFlow = GetFlag(FEATURE_FLAG_CROSS_SELL_DYNAMIC);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [itemsTable, setItemsTable] = useState<ItemTableProps>({
    items: [],
    page: 1,
    size: 12
  });
  const [validationModalInformation, setValidationModalInformation] =
    useState<ValidationModalProps>({
      show: false,
      title: '',
      description: '',
      handleNextBtn: () => null,
      handleNextBtnTitle: 'Continuar',
      hiddenLeftButton: false,
      hasCloseBtn: false
    });

  const [changePaymentMethodInformation, setChangePaymentMethodInformation] =
    useState<ChangePaymentMethodModalProps>({
      show: false,
      title: '',
      description: '',
      handleNextBtn: () => null,
      handleNextBtnTitle: 'Continuar',
      hiddenLeftButton: false,
      hasCloseBtn: false
    });
  const [cartLoading, setCartLoading] = useState(false);
  const [allItemsFailed, setAllItemsFailed] = useState(false);

  const { favoritedSkus, handleToggleFavorite: handleCartWishlistToggle } =
    useCartWishlist(cartItems);

  const { setLoadingModalOpen, shouldShowOnlyLoading } = useLoadingModal();

  useEffect(() => {
    setLoadingModalOpen(cartLoading);
  }, [cartLoading, setLoadingModalOpen]);

  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const isManager = useSelector(
    (appState: ApplicationState) => appState.user.isManager
  );
  const shoppingCart = useSelector(
    (appState: ApplicationState) => appState.longSteelShoppingCart
  );
  const checkoutState = useSelector(
    (appState: ApplicationState) => appState.orderSimulateCheckoutMvp
  );
  const crossSellState = useSelector(
    (appState: ApplicationState) => appState.crossSell
  );

  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCart.emitterId
  ) as ICustomer;

  const receiverCartInfo = useSelector((state: ApplicationState) =>
    (state.entryOrderReceivers?.receivers || []).find(
      (r: IShipToCustomerItem) => r.customerId === shoppingCart.receiverId
    )
  );

  const isBlockedCustomer =
    customer?.blocks &&
    customer?.blocks?.length > 0 &&
    !!customer.blocks.find((blc) => blc.type === 'financial');

  const paymentsTermAllowed = cartItems
    .map((opt) => opt.cartOptions)
    .reduce((acc: CartOptions[], item: NewCartOption) => {
      if (!item) return acc;
      const options = Object.values(item);
      return [...acc, ...options];
    }, [])
    .map((opt) => opt.paymentTerms)
    .reduce((acc, item) => {
      if (!item) return acc;
      return [...item, ...acc];
    }, [])
    .reduce((accAll: PaymentTerm[], item: PaymentTerm) => {
      if (accAll.some((accItem) => accItem.term === item.term)) {
        return accAll;
      }

      return [...accAll, item];
    }, []);

  const hasItemWithoutPaymentTermsAllowed =
    cartItems
      .map((item) => item.extension_attributes.payment_type_gerdau || '')
      .filter(
        (item) =>
          !paymentsTermAllowed
            .map((item: PaymentTerm) => item.term)
            .includes(item)
      )?.length || 0;

  const dispatch = useDispatch();

  const commerceApi = useApiInstance();

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById;

  const loading = cartLoading || customerLoading;

  const handleServiceUnavailable = ({
    noClean
  }: { noClean?: boolean } = {}) => {
    dispatch(
      sendTagging({
        event: 'servico_indisponivel',
        screenName: 'carrinho',
        tipo_compra: tipo_compra,
        mensagem: 'Serviço indisponível temporariamente'
      })
    );

    setValidationModalInformation({
      title: 'Serviço indisponível temporariamente',
      description:
        'Tente novamente mais tarde ou entre em contato com o seu vendedor.',
      show: true,
      hiddenLeftButton: true,
      handleNextBtn: handleCloseValidationModal,
      handleNextBtnTitle: 'Continuar'
    });

    if (noClean) {
      return;
    }
    setCartItems([]);
    setDirtyCartItems([]);
    setItemsTable((prev) => ({
      page: 1,
      size: prev.size,
      items: []
    }));
    setSelectedItems([]);
    setGoToCheckout(false);
  };

  useEffect(() => {
    if (cartInitializedRef.current) return;
    (async () => {
      try {
        setCartLoading(true);

        if (crossSellFeatureFlag && crossSellKey === '') {
          dispatch(cleanCrossSellItems());
        }

        const bodyRequest = {
          soldTo: shoppingCart.emitterId,
          shipTo: shoppingCart.receiverId
        };

        const hasBodyKeyEmpty = Object.values(bodyRequest).some(
          (item) => !item
        );

        if (
          !customerLoading &&
          !hasBodyKeyEmpty &&
          cartId &&
          (isManager || customersState.customers.length > 0)
        ) {
          cartInitializedRef.current = true;
          const data = await cartData();

          if (!data || data.hasError) {
            throw new Error();
          }

          if (data && Array.isArray(data.items)) {
            let cartProducts: NewCartItem[] = [];
            if (newCartEndpoint) {
              cartProducts = formatCartItems(data.items);
            } else {
              cartProducts = await getProductsInformation({
                items: data.items
              });
            }

            if (cartProducts.length !== data.items.length) {
              throw new Error();
            }

            const slicedItems = sliceItems({
              cartItems: cartProducts,
              page: 1,
              size: itemsTable.size
            });

            cartProducts.map((product) => {
              if (product.item_id && product.sku) {
                migrateTipoCompraFromSkuToItemId(
                  String(product.sku),
                  product.item_id
                );
              }
              if (
                product.price.salesAgreementData.taxBenefit &&
                !product.extension_attributes.tax_benefit_gerdau &&
                product.cartOptions[
                  product.extension_attributes.destination_gerdau
                ].fiscalBenefits.length
              ) {
                product.extension_attributes.tax_benefit_gerdau =
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].fiscalBenefits[0].code;
              }
              return product;
            });

            setCartItems(cartProducts);
            setDirtyCartItems(cartProducts);
            setItemsTable((prev) => ({
              page: 1,
              size: prev.size,
              items: slicedItems
            }));
            setSelectedItems([]);
            setMinimunOrder(data.minimum ?? []);

            const taggingProduct: ITaggingParams = {
              event: 'view_cart',
              ecommerce: {
                currency: 'BRL',
                value: getTotal(cartProducts),
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                paginacao: itemsTable.page.toString(),
                items: cartProducts.map((item) => ({
                  screenName: 'carrinho',
                  tipo_compra: getTipoCompraForItemId(
                    item.item_id ?? 0,
                    purchaseOrigin(),
                    item.sku?.toString()
                  ),
                  item_id: item?.sku?.toString(),
                  item_name: item.name,
                  price: item.price?.creditPrice,
                  unidade_de_medida:
                    item.extension_attributes.measure_unity_gerdau,
                  quantity: item.qty.toString(),
                  data_desejada: item.extension_attributes.expected_date_gerdau,
                  pagamento: item.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    item.extension_attributes.tax_benefit_gerdau,
                  destinacao: item.extension_attributes.destination_gerdau,
                  item_category: item?.descricao_gpm ?? '',
                  favoritado:
                    item.extension_attributes?.added_to_wishlist ?? false
                }))
              }
            };
            if (crossSellFeatureFlag && crossSellKey === '') {
              dispatch(
                checkCrossSellAvailable({
                  cartItems: crossSellNewFlow ? undefined : cartProducts,
                  divisions: crossSellNewFlow
                    ? undefined
                    : customer?.division || [],
                  businessPartners: [shoppingCart.receiverId],
                  soldTo:
                    shoppingCart.emitterId !== shoppingCart.receiverId
                      ? shoppingCart.emitterId
                      : undefined,
                  commerceApi,
                  useNewFlow: crossSellNewFlow
                })
              );
            }
            dispatch(sendTagging(taggingProduct));
            return;
          }

          throw new Error();
        }
      } catch (error) {
        console.error(error);
        cartInitializedRef.current = false;
        handleServiceUnavailable();
      } finally {
        setCartLoading(false);
      }
    })();
  }, [customerLoading]);

  useEffect(() => {
    if (dirtyCartItems) {
      const hasBlockedPaymentType = dirtyCartItems.some(
        (item) =>
          !paymentAnticipated.includes(
            item.extension_attributes?.payment_type_gerdau
          )
      );

      const hasExistingPaymentIssue =
        (isBlockedCustomer && hasBlockedPaymentType) ||
        hasItemWithoutPaymentTermsAllowed > 0;

      const hasPaymentMismatch = !hasCommonPaymentType(dirtyCartItems);

      if (hasExistingPaymentIssue || hasPaymentMismatch) {
        const isIncompatible = hasPaymentMismatch && !hasExistingPaymentIssue;

        if (newCartEndpoint) {
          setChangePaymentMethodInformation({
            title: isIncompatible
              ? 'Condição de pagamento incompatível'
              : 'A condição de pagamento está indisponível',
            description: isIncompatible
              ? 'Os produtos no seu carrinho possuem condições de pagamento diferentes ou indisponíveis. Selecione uma condição compatível com todos os itens:'
              : 'Altere a condição de pagamento do seu carrinho para continuar:',
            show: true,
            hiddenLeftButton: true,
            handleNextBtn: handleCloseValidationModal,
            handleNextBtnTitle: 'Alterar',
            hasCloseBtn: true
          });
          return;
        }

        setValidationModalInformation({
          title: isIncompatible
            ? 'Condição de pagamento incompatível'
            : 'Condição de pagamento indisponível',
          description: isIncompatible
            ? 'Os produtos no seu carrinho possuem condições de pagamento diferentes. Alinhe as condições de pagamento antes de continuar.'
            : 'Selecione uma condição de pagamento antecipada ou entre em contato com o seu vendedor.',
          show: true,
          hiddenLeftButton: true,
          handleNextBtn: handleCloseValidationModal,
          handleNextBtnTitle: 'Continuar'
        });
      }
    }
  }, [isBlockedCustomer, dirtyCartItems]);

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const selectedProducts = cartItems.map((product) => product.item_id ?? 0);

      setSelectedItems(selectedProducts);
      return;
    }

    setSelectedItems([]);
    return;
  };

  const handleSelectProduct = (
    e: ChangeEvent<HTMLInputElement>,
    item_id: number
  ) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, item_id]);
      return;
    }

    const selectedProducts = selectedItems.filter(
      (selected) => selected !== item_id
    );
    setSelectedItems(selectedProducts);
    return;
  };

  const getTotal = (items: NewCartItem[]) => {
    const total = items.reduce((total, item) => {
      const destinationOptions = Object.values(item.cartOptions).map(
        (destinationOpt: CartOptions) => ({
          value: destinationOpt.destination.code,
          label: destinationOpt.destination.description
        })
      );

      const destination =
        item.extension_attributes?.destination_gerdau ??
        destinationOptions[0].value;

      const optionsByDestination = item.cartOptions[destination];

      const price = item.extension_attributes.price_gerdau
        ? JSON.parse(item.extension_attributes.price_gerdau)
        : item.price;

      const unity = item.extension_attributes.measure_unity_gerdau;
      const currentMeasureUnit = optionsByDestination.measureUnits.find(
        (unit) => unit.measure === unity
      ) as MeasureUnit;

      const productDate = item.extension_attributes.expected_date_gerdau;
      const salesAgreementData = price.salesAgreementData;
      const endDate = salesAgreementData.expirationDate
        ? salesAgreementData.expirationDate.replace('Z', '')
        : salesAgreementData.expirationDate;

      const salesAgreementExpirationDate =
        !moment(productDate).isAfter(endDate);
      const agreementData = {
        destination: salesAgreementData.destination,
        payment: salesAgreementData.paymentTerms,
        expirationDate: endDate
      };

      const hasAgreement = () => {
        const hasAnyAgreementKeyEmpty = Object.values(agreementData).some(
          (item) => !item
        );
        if (salesAgreementData && !hasAnyAgreementKeyEmpty) {
          return (
            salesAgreementData.destination === destination &&
            salesAgreementData.paymentTerms ===
              item.extension_attributes.payment_type_gerdau &&
            salesAgreementExpirationDate
          );
        }

        return false;
      };

      const priceWithoutMeasure =
        hasAgreement() && salesAgreementData.salesAgreement
          ? salesAgreementData.salesAgreement
          : Number(price.totalPrice ?? price.creditPrice) /
            normalizeQuantity(price.quantity);

      const convertPriceMeasure = optionsByDestination.measureUnits.find(
        (priceOpt) => priceOpt.measure === price.um
      );

      const priceKg =
        priceWithoutMeasure /
        parseNumber(convertPriceMeasure?.convMeasureKg ?? '1');

      const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
      const minimunOrderQty =
        measureUnitminQty !== 1 && item.qty === 1
          ? Math.ceil(measureUnitminQty / Number(currentMeasureUnit.multiple)) *
            Number(currentMeasureUnit.multiple)
          : Number(currentMeasureUnit.multiple) * item.qty;

      const multipleWithKG =
        parseNumber(currentMeasureUnit.convMeasureKg ?? '1') * minimunOrderQty;

      const itemPrice = priceKg * multipleWithKG;

      return total + itemPrice;
    }, 0);
    return total.toFixed(2);
  };

  const handleChangePagination = (page: number, size: number) => {
    const slicedItems = sliceItems({ cartItems, page, size });
    setItemsTable({ items: slicedItems, page, size });
  };

  const handleUpdateItem = async ({
    item_id,
    type,
    value
  }: UpdateItemProps) => {
    const item = cartItems.find((cartItem) => cartItem.item_id === item_id);
    if (!value || !item) {
      return;
    }
    updateDirtyCartState(
      dirtyCart.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      )
    );
    // setViewCartTag(true);
    setItemsTable((prev) => ({
      ...itemsTable,
      items: prev.items.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      )
    }));
    setCartItems((cartItemsPrev) => {
      return cartItemsPrev.map((itemPrev) =>
        itemPrev.item_id === item.item_id
          ? {
              ...itemPrev,
              ...(type === 'chip' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  measure_unity_gerdau: value as string
                }
              }),
              ...(type === 'quantity' && { qty: value as number }),
              ...(type === 'date' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  expected_date_gerdau: moment(value).format('YYYY-MM-DD')
                },
                errors: {
                  ...itemPrev.errors,
                  date: false
                }
              }),
              ...(type === 'destination' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  destination_gerdau: value as string
                },
                errors: {
                  ...itemPrev.errors,
                  destination: false
                }
              }),
              ...(type === 'payment' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  payment_type_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  payment: false
                }
              }),
              ...(type === 'taxBenefit' && {
                extension_attributes: {
                  ...itemPrev.extension_attributes,
                  tax_benefit_gerdau: String(value)
                },
                errors: {
                  ...itemPrev.errors,
                  taxBenefit: false
                }
              })
            }
          : itemPrev
      );
    });
  };

  const applyBulkErrorState = (
    items: NewCartItem[],
    globalErrors: string[]
  ): boolean => {
    const { hasErrors, allFailed } = dispatchBulkErrorToast(
      items,
      globalErrors
    );
    setAllItemsFailed(hasErrors && allFailed);
    return hasErrors;
  };

  const handleDuplicateItem = async (id: number) => {
    if (validations.maximum || maxItemsV) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho atingiu o limite de 30 produtos.'
        })
      );
      return dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho atingiu o limite de 30 produtos.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    }

    try {
      setCartLoading(true);

      const item = cartItems.find(
        (cartItem) => cartItem.item_id === id
      ) as NewCartItem;

      const itemIndex =
        cartItems.findIndex((cartItem) => cartItem.item_id === id) ?? 0;

      const itemToDuplicate = {
        ...item,
        extension_attributes: {
          ...item.extension_attributes,
          expected_date_gerdau: ''
        }
      };

      // delete item_id to method BULK create a new item on cart with item_id
      delete itemToDuplicate.item_id;

      cartItems.splice(itemIndex + 1, 0, itemToDuplicate);

      const newItems = cartItems.map((product) => {
        const extensionAtt = product.extension_attributes;
        const destination = extensionAtt.destination_gerdau;
        const cartOptions = product.cartOptions[destination];
        const itemUnits = cartOptions.measureUnits;

        const unit = product.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;
        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && product.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * product.qty * 1000
              ) / 1000;

        return {
          ...product,
          qty: minimunOrderQty
        };
      });

      const data = await addBulkGetCart({
        items: newItems,
        minimumExisting: minimunOrder
      });

      if (
        (data && data.cart && data.cart.hasError) ||
        data.delete !== true ||
        typeof data.delete === 'object' ||
        !data
      ) {
        throw new Error();
      }

      if (data && Array.isArray(data.cart.items)) {
        let cartProducts: NewCartItem[] = [];
        if (newCartEndpoint) {
          cartProducts = formatCartItems(data.cart.items);
        } else {
          cartProducts = await getProductsInformation({
            items: data.cart.items
          });
        }

        if (cartProducts.length !== data.cart.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.cart.minimum ?? []);
        applyBulkErrorState(cartProducts, data.errors ?? []);
        return;
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      setCartLoading(true);
      setAllItemsFailed(false);

      //Return true when has sucess on delete
      const removeItem = await deleteItem(id);

      //Case has error show error modal
      if (
        removeItem !== true ||
        !removeItem ||
        typeof removeItem === 'object'
      ) {
        throw new Error();
      }

      //If has only 1 item and removeItem is sucess then clean cart
      if (cartItems.length === 1) {
        setCartItems([]);
        setDirtyCartItems([]);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: []
        }));
        setSelectedItems([]);

        //Case has sucess dispatch tagging
        dispatch(
          sendTagging({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'BRL',
              value: '0',
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              items: cartItems
                .filter((cartItem) => cartItem.item_id === id)
                .map((deletedProduct) => ({
                  screenName: 'visualizacao_carrinho',
                  tipo_compra: crossSellState.addedSkus.includes(
                    String(deletedProduct.sku)
                  )
                    ? 'cross_sell'
                    : tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
            }
          })
        );
        return;
      }

      const items = cartItems.filter((cartItem) => cartItem.item_id !== id);

      //Case has sucess dispatch tagging
      dispatch(
        sendTagging({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'BRL',
            value: getTotal(items),
            screenName: 'carrinho',
            tipo_compra: tipo_compra,
            items: cartItems
              .filter((cartItem) => cartItem.item_id === id)
              .map((deletedProduct) => ({
                screenName: 'visualizacao_carrinho',
                tipo_compra: crossSellState.addedSkus.includes(
                  String(deletedProduct.sku)
                )
                  ? 'cross_sell'
                  : tipo_compra,
                item_id: deletedProduct?.sku?.toString(),
                item_name: deletedProduct.name,
                price: deletedProduct.price?.creditPrice,
                unidade_de_medida:
                  deletedProduct.extension_attributes.measure_unity_gerdau,
                quantity: deletedProduct.qty.toString(),
                data_desejada:
                  deletedProduct.extension_attributes.expected_date_gerdau,
                pagamento:
                  deletedProduct.extension_attributes.payment_type_gerdau,
                beneficio_fiscal:
                  deletedProduct.extension_attributes.tax_benefit_gerdau,
                destinacao:
                  deletedProduct.extension_attributes.destination_gerdau,
                item_category: deletedProduct.product_type
              }))
          }
        })
      );

      // Fetch fresh cart data to get updated items and minimum order info
      const data = await cartData();

      //Case has error show error modal
      if (!data || data.hasError) {
        throw new Error();
      }

      //Case has sucess get information from products on magento API
      if (data && Array.isArray(data.items)) {
        let cartProducts: NewCartItem[] = [];
        if (newCartEndpoint) {
          cartProducts = formatCartItems(data.items);
        } else {
          cartProducts = await getProductsInformation({
            items: data.items
          });
        }

        //Case return with error show error modal
        if (cartProducts.length !== data.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.minimum ?? []);
        return;
      }

      //Show modal error when no has data or dont is a Array on data.items
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleDeleteAllItems = async () => {
    try {
      setCartLoading(true);
      setAllItemsFailed(false);
      //Case mark to delete all items
      if (cartItems.length === selectedItems.length) {
        const data = await deleteAllCartItems();

        //Case return error on try delete method
        if (data.delete !== true || !data || typeof data.delete === 'object') {
          throw new Error();
        }

        //Case has sucess dispatch tagging and clean cart
        if (data.delete) {
          dispatch(
            sendTagging({
              event: 'remove_from_cart',
              ecommerce: {
                currency: 'BRL',
                value: getTotal(cartItems),
                screenName: 'carrinho',
                tipo_compra: tipo_compra,
                items: cartItems.map((deletedProduct) => ({
                  screenName: 'carrinho',
                  tipo_compra: tipo_compra,
                  item_id: deletedProduct?.sku?.toString(),
                  item_name: deletedProduct.name,
                  price: deletedProduct.price?.creditPrice,
                  unidade_de_medida:
                    deletedProduct.extension_attributes.measure_unity_gerdau,
                  quantity: deletedProduct.qty.toString(),
                  data_desejada:
                    deletedProduct.extension_attributes.expected_date_gerdau,
                  pagamento:
                    deletedProduct.extension_attributes.payment_type_gerdau,
                  beneficio_fiscal:
                    deletedProduct.extension_attributes.tax_benefit_gerdau,
                  destinacao:
                    deletedProduct.extension_attributes.destination_gerdau,
                  item_category: deletedProduct.product_type
                }))
              }
            })
          );
          setCartItems([]);
          setDirtyCartItems([]);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: []
          }));
          setSelectedItems([]);
        }
        return;
      }

      //Case NOT mark all items to delete - delete only selected items
      const itemsToDelete = cartItems.filter(
        (item) =>
          item.item_id &&
          selectedItems.find((tableItem) => tableItem === item.item_id)
      );

      const itemsWithoutDelete = cartItems.filter(
        (item) =>
          item.item_id &&
          !selectedItems.find((tableItem) => tableItem === item.item_id)
      );

      if (itemsWithoutDelete.length === 0) {
        const data = await deleteAllCartItems();
        if (data.delete !== true || !data || typeof data.delete === 'object') {
          throw new Error();
        }
        if (data.delete) {
          setCartItems([]);
          setDirtyCartItems([]);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: []
          }));
          setSelectedItems([]);
        }
        setCartLoading(false);
        return;
      }

      const itemIdsToDelete = itemsToDelete
        .map((item) => item.item_id)
        .filter((id): id is number => id !== undefined);

      const deleteSuccess = await deleteBulkItems(itemIdsToDelete);

      //Case has error show error modal
      if (!deleteSuccess) {
        throw new Error();
      }

      //Dispatch tagging with items deleted
      dispatch(
        sendTagging({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'BRL',
            value: getTotal(itemsToDelete),
            screenName: 'carrinho',
            tipo_compra: tipo_compra,
            items: itemsToDelete.map((deletedProduct) => ({
              screenName: 'carrinho',
              tipo_compra: tipo_compra,
              item_id: deletedProduct?.sku?.toString(),
              item_name: deletedProduct.name,
              price: deletedProduct.price?.creditPrice,
              unidade_de_medida:
                deletedProduct.extension_attributes.measure_unity_gerdau,
              quantity: deletedProduct.qty.toString(),
              data_desejada:
                deletedProduct.extension_attributes.expected_date_gerdau,
              pagamento:
                deletedProduct.extension_attributes.payment_type_gerdau,
              beneficio_fiscal:
                deletedProduct.extension_attributes.tax_benefit_gerdau,
              destinacao:
                deletedProduct.extension_attributes.destination_gerdau,
              item_category: deletedProduct.product_type
            }))
          }
        })
      );

      // Fetch fresh cart data to get updated items and minimum order info
      const data = await cartData();

      //Case has error show error modal
      if (!data || data.hasError) {
        throw new Error();
      }

      if (data && Array.isArray(data.items)) {
        let cartProducts: NewCartItem[] = [];
        if (newCartEndpoint) {
          cartProducts = formatCartItems(data.items);
        } else {
          cartProducts = await getProductsInformation({
            items: data.items
          });
        }

        //Case return from products informations length dont is equal to cart length
        if (cartProducts.length !== data.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.minimum ?? []);
        return;
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
    }
  };

  const handleUpdateAllItems = async ({
    date,
    payment,
    fiscalBenefits,
    destination,
    updatePrice,
    closeWhenSucess
  }: OptionsProps) => {
    try {
      setCartLoading(true);

      //If call this function to update price options;
      if (updatePrice) {
        //Prepare data to update all stats from carts items
        const newItems = cartItems.map((product) => {
          const extensionAtt = product.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = product.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = product.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && product.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * product.qty * 1000
                ) / 1000;

          return {
            ...product,
            qty: minimunOrderQty
          };
        });

        //Call function to update all
        const data = await addBulkGetCart({
          items: newItems,
          minimumExisting: minimunOrder
        });

        //When has any error on update function show modal error
        if (
          (data && data.cart && data.cart.hasError) ||
          data.delete !== true ||
          typeof data.delete === 'object' ||
          !data
        ) {
          throw new Error();
        }

        //if return sucess enter on step to get information of product
        if (data && Array.isArray(data.cart.items)) {
          let cartProducts: NewCartItem[] = [];
          if (newCartEndpoint) {
            cartProducts = formatCartItems(data.cart.items);
          } else {
            cartProducts = await getProductsInformation({
              items: data.cart.items
            });
          }

          //When has any error on get information show modal error
          if (cartProducts.length !== data.cart.items.length) {
            throw new Error();
          }

          const slicedItems = sliceItems({
            cartItems: cartProducts,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(cartProducts);
          setDirtyCartItems(cartProducts);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);
          setMinimunOrder(data.cart.minimum ?? []);
          applyBulkErrorState(cartProducts, data.errors ?? []);
          return;
        }

        //When is updatePrice and dont match with anyway
        throw new Error();
      }

      const allItemsSelected =
        selectedItems.length === cartItems.length || closeWhenSucess;

      const itemsAdjust = cartItems.filter((cart) => {
        if (allItemsSelected) {
          return true;
        }

        return cart.item_id && selectedItems.includes(cart.item_id);
      });

      const newItems = [...itemsAdjust].map((item) => {
        const canChangeDestination =
          destination &&
          Object.keys(item.cartOptions).some((dest) => dest === destination);

        const canChangeFiscalBenefits =
          fiscalBenefits &&
          item.cartOptions[
            item.extension_attributes.destination_gerdau
          ].fiscalBenefits.some((fiscal) => fiscal.code === fiscalBenefits);

        return {
          ...item,
          extension_attributes: {
            ...item.extension_attributes,
            ...(date && {
              expected_date_gerdau: moment(date).format('YYYY-MM-DD')
            }),
            ...(payment && { payment_type_gerdau: payment }),
            ...(destination &&
              canChangeDestination && { destination_gerdau: destination }),
            ...(fiscalBenefits &&
              canChangeFiscalBenefits && { tax_benefit_gerdau: fiscalBenefits })
          }
        };
      });

      const oldItems = cartItems.filter(
        (old) => old.item_id && !selectedItems.includes(old.item_id)
      );

      const newCartItems = [
        ...newItems,
        ...(allItemsSelected ? [] : oldItems)
      ].map((product) => {
        const extensionAtt = product.extension_attributes;
        const destination = extensionAtt.destination_gerdau;
        const cartOptions = product.cartOptions[destination];
        const itemUnits = cartOptions.measureUnits;

        const unit = product.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;
        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && product.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * product.qty * 1000
              ) / 1000;

        return {
          ...product,
          qty: minimunOrderQty
        };
      });

      const data = await addBulkGetCart({
        items: newCartItems,
        minimumExisting: minimunOrder
      });

      if (
        (data && data.cart && data.cart.hasError) ||
        data.delete !== true ||
        typeof data.delete === 'object' ||
        !data
      ) {
        throw new Error();
      }

      if (data && Array.isArray(data.cart.items)) {
        let cartProducts: NewCartItem[] = [];
        if (newCartEndpoint) {
          cartProducts = formatCartItems(data.cart.items);
        } else {
          cartProducts = await getProductsInformation({
            items: data.cart.items
          });
        }

        if (cartProducts.length !== data.cart.items.length) {
          throw new Error();
        }

        const slicedItems = sliceItems({
          cartItems: cartProducts,
          page: 1,
          size: itemsTable.size
        });

        setCartItems(cartProducts);
        setDirtyCartItems(cartProducts);
        setItemsTable((prev) => ({
          page: 1,
          size: prev.size,
          items: slicedItems
        }));
        setSelectedItems([]);
        setMinimunOrder(data.cart.minimum ?? []);
        applyBulkErrorState(cartProducts, data.errors ?? []);
        return;
      }

      //Case dont match with anyway
      throw new Error();
    } catch (error) {
      console.error(error);
      handleServiceUnavailable();
    } finally {
      setCartLoading(false);
      if (closeWhenSucess) {
        setChangePaymentMethodInformation({
          ...changePaymentMethodInformation,
          show: false
        });
      }
    }
  };

  const handleUpdateSingleItem = async (product: NewCartItem) => {
    try {
      setCartLoading(true);

      const extensionAtt = product.extension_attributes;
      const destination = extensionAtt.destination_gerdau;
      const cartOptions = product.cartOptions[destination];
      const itemUnits = cartOptions.measureUnits;

      const unit = product.extension_attributes
        .measure_unity_gerdau as EMeasureUnit;
      const currentMeasureUnit = itemUnits.find(
        (unity) => unity.measure === unit
      ) as MeasureUnit;

      const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
      const minimunOrderQty =
        measureUnitminQty !== 1 && product.qty === 1
          ? Math.round(
              Math.ceil(
                measureUnitminQty / Number(currentMeasureUnit.multiple)
              ) *
                Number(currentMeasureUnit.multiple) *
                1000
            ) / 1000
          : Math.round(
              Number(currentMeasureUnit.multiple) * product.qty * 1000
            ) / 1000;

      const updatedProduct = {
        ...product,
        qty: minimunOrderQty
      };

      const response = await updateCartItem({
        item: updatedProduct
      });

      if (!response || response?.error) {
        throw new Error();
      }

      // Fetch full cart items with quantity normalization via cartData
      const data = await cartData({ minimumExisting: minimunOrder });

      if (!data || data.hasError || !Array.isArray(data.items)) {
        throw new Error();
      }

      let cartProducts: NewCartItem[] = [];
      if (newCartEndpoint) {
        cartProducts = formatCartItems(data.items);
      } else {
        cartProducts = await getProductsInformation({
          items: data.items
        });
      }

      if (cartProducts.length !== data.items.length) {
        throw new Error();
      }

      const slicedItems = sliceItems({
        cartItems: cartProducts,
        page: itemsTable.page,
        size: itemsTable.size
      });

      setCartItems(cartProducts);
      setDirtyCartItems(cartProducts);
      setItemsTable((prev) => ({
        ...prev,
        items: slicedItems
      }));
      setMinimunOrder(data.minimum ?? []);

      dispatch(
        addToast({
          type: EToastTypes.SUCCESS,
          message: 'Preço atualizado com sucesso',
          durationInMS: 3000
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao atualizar o preço do item',
          durationInMS: 5000
        })
      );
    } finally {
      setCartLoading(false);
    }
  };

  const generateGraph = (products: NewCartItem[]) => {
    const minimunOrderRules = minimunOrder.map((min) => ({
      unit: min.unit,
      type: min.gpm ?? 'Peso do carrinho',
      name: min.name ?? 'Peso do carrinho',
      minQty: Math.floor(min.qty),
      cartQty: 0,
      partial: min.partial,
      hasOrderItem: min.hasOrderItem
    }));

    const cartHeaderGraphData: CartHeaderGraph[] = products.reduce(
      (acc: CartHeaderGraph[], item) => {
        const cartQty = item.qty;
        const minQty = 0;
        const type = item.sap_descricao_gpm ?? 'Peso do carrinho';
        const partial = item.partial;
        const hasOrderItem = item.hasOrderItem ? item.hasOrderItem : false;

        const destinationOptions = Object.values(item.cartOptions).map(
          (destinationOpt: CartOptions) => destinationOpt.destination.code
        );
        const destination =
          item.extension_attributes?.destination_gerdau ??
          destinationOptions[0];

        if (Object.values(item.cartOptions).length === 0) {
          return [];
        }

        const optionsByDestination = item.cartOptions[destination];
        const itemUnits = optionsByDestination.measureUnits;
        const unit = item.extension_attributes
          .measure_unity_gerdau as EMeasureUnit;

        const currentMeasureUnit = itemUnits.find(
          (unity) => unity.measure === unit
        ) as MeasureUnit;

        const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
        const minimunOrderQty =
          measureUnitminQty !== 1 && item.qty === 1
            ? Math.round(
                Math.ceil(
                  measureUnitminQty / Number(currentMeasureUnit.multiple)
                ) *
                  Number(currentMeasureUnit.multiple) *
                  1000
              ) / 1000
            : Math.round(
                Number(currentMeasureUnit.multiple) * item.qty * 1000
              ) / 1000;

        const hasItemType = minimunOrderRules.some(
          (minItem) => minItem.type === type
        );

        const newItem = {
          cartQty,
          minQty,
          unit,
          type: hasItemType ? type : 'Peso do carrinho',
          partial,
          hasOrderItem
        };

        const existingItem = acc.find(
          (accItem) => accItem.type === newItem.type
        );

        if (
          !currentMeasureUnit ||
          !currentMeasureUnit.convMeasureKg ||
          !currentMeasureUnit.convMeasureTo
        ) {
          return acc;
        }

        if (existingItem) {
          const minimunKg =
            minimunOrderQty * parseNumber(currentMeasureUnit.convMeasureKg);
          const convertType =
            existingItem?.unit.toUpperCase() === 'KG'
              ? minimunKg
              : minimunKg / 1000;
          existingItem.cartQty += convertType;
        }

        return acc;
      },
      minimunOrderRules
    );

    if (minimunOrder.length > 0 && cartHeaderGraphData.length === 0) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Falha ao buscar as opções dos produtos'
        })
      );
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar as opções dos produtos',
          labelActionOne: 'Atualizar',
          handleClickActionOne: () => history.go(0),
          durationInMS: 5000
        })
      );
    }

    return cartHeaderGraphData;
  };

  const graphCartItems = generateGraph(cartItems);
  const maxItemsV = cartItems.length === 30;
  const minItemV = graphCartItems.some(
    (graph) => graph.minQty > graph.cartQty && !graph.partial
  );
  const partialItemsV = graphCartItems.some(
    (it) =>
      it.partial !== undefined &&
      it.partial &&
      it.minQty > it.cartQty &&
      it.hasOrderItem
  );

  const resultPartial = partialItemsV;
  const resultMin = !partialItemsV && minItemV;
  const resMaximum = !resultMin && !resultPartial && maxItemsV; // prevent show both
  const validations = {
    partial: resultPartial,
    minimum: resultMin,
    maximum: resMaximum
  };

  const handleCloseValidationModal = () => {
    setValidationModalInformation((prev) => ({
      ...prev,
      show: false
    }));
  };

  const handleNavigateSelectingItems = () => {
    history.push(`${EOrderSettingsRoutes.SEARCH_ITEMS}`);
  };

  const handleNext = async () => {
    const multipleSameProducts = hasDuplicates(cartItems);

    const maxDeliveryDate = moment().add(2, 'years');
    const itemsCheckErrors: NewCartItem[] = cartItems.map((item) => {
      const withoutDate = !item.extension_attributes.expected_date_gerdau;
      const dateExceedsMax =
        !!item.extension_attributes.expected_date_gerdau &&
        moment(item.extension_attributes.expected_date_gerdau).isAfter(
          maxDeliveryDate
        );
      const withoutPayment = !item.extension_attributes.payment_type_gerdau;
      const withoutDestination = !item.extension_attributes.destination_gerdau;

      const paymentBlocked =
        (!!isBlockedCustomer &&
          !paymentAnticipated.includes(
            item.extension_attributes.payment_type_gerdau
          )) ||
        hasItemWithoutPaymentTermsAllowed > 0;

      if (
        withoutDate ||
        dateExceedsMax ||
        withoutPayment ||
        paymentBlocked ||
        withoutDestination
      ) {
        return {
          ...item,
          errors: {
            ...item.errors,
            date: withoutDate || dateExceedsMax,
            payment: withoutPayment || paymentBlocked,
            destination: withoutDestination
          }
        };
      }

      return item;
    });

    const itemsWithErrors = itemsCheckErrors.filter((item) =>
      Object.values(item.errors as ErrorProps).some((error) => error)
    );

    if (itemsWithErrors.length) {
      const cartItemsSort = itemsCheckErrors.sort((a, b) => {
        const aHasError = Object.values(a.errors as ErrorProps).some(
          (error) => error
        );
        const bHasError = Object.values(b.errors as ErrorProps).some(
          (error) => error
        );

        if (aHasError && !bHasError) return -1;
        if (!aHasError && bHasError) return 1;
        return 0;
      });

      const slicedItems = sliceItems({
        cartItems: cartItemsSort,
        page: 1,
        size: itemsTable.size
      });

      setCartItems(cartItemsSort);
      setDirtyCartItems(cartItemsSort);
      setItemsTable((prev) => ({
        page: 1,
        size: prev.size,
        items: slicedItems
      }));
      setSelectedItems([]);

      // setViewCartTag(true);
      if (shoppingCartRef.current) {
        shoppingCartRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      const hasItemsExceedingMaxDate = itemsCheckErrors.some(
        (item) =>
          !!item.extension_attributes.expected_date_gerdau &&
          moment(item.extension_attributes.expected_date_gerdau).isAfter(
            maxDeliveryDate
          )
      );
      if (hasItemsExceedingMaxDate) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message:
              'A data desejada excede o prazo permitido. Escolha uma data dentro dos próximos 2 anos.',
            durationInMS: 5000
          })
        );
        return;
      }
      if (itemsWithErrors.find((err) => err.errors?.payment)) {
        dispatch(
          sendTagging({
            event: 'pagamento_indisponivel',
            screenName: 'carrinho',
            tipo_compra: tipo_compra
          })
        );
        if (!newCartEndpoint) {
          setValidationModalInformation({
            title: 'Condição de pagamento indisponível',
            description:
              'Selecione uma condição de pagamento antecipada ou entre em contato com o seu vendedor.',
            show: true,
            hiddenLeftButton: true,
            handleNextBtn: handleCloseValidationModal,
            handleNextBtnTitle: 'Continuar'
          });
        }

        return;
      }
      return;
    }

    if (validations.minimum) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho precisa de uma quantidade mínima de produtos.'
        })
      );

      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho precisa de uma quantidade mínima de produtos.',
          labelActionOne: 'Adicionar produto',
          handleClickActionOne: () => handleNavigateSelectingItems(),
          durationInMS: 5000
        })
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (validations.partial) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho precisa de uma quantidade mínima de produtos.'
        })
      );

      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho precisa de uma quantidade mínima de produtos.',
          labelActionOne: 'Adicionar produto',
          handleClickActionOne: () => handleNavigateSelectingItems(),
          durationInMS: 5000
        })
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (multipleSameProducts) {
      setValidationModalInformation({
        hiddenLeftButton: false,
        show: true,
        title: 'Produtos idênticos encontrados',
        description:
          'As quantidades dos produtos idênticos serão somadas. Deseja continuar?',
        handleNextBtn: handleToCheckout,
        handleNextBtnTitle: 'Continuar'
      });

      return;
    }

    if (!hasCommonPaymentType(cartItems)) {
      if (newCartEndpoint) {
        setChangePaymentMethodInformation({
          title: 'Condição de pagamento incompatível',
          description:
            'Os produtos no seu carrinho possuem condições de pagamento diferentes ou indisponíveis. Selecione uma condição compatível com todos os itens:',
          show: true,
          hiddenLeftButton: false,
          handleNextBtn: handleCloseValidationModal,
          handleNextBtnTitle: 'Alterar',
          hasCloseBtn: true
        });
      } else {
        setValidationModalInformation({
          title: 'Condição de pagamento incompatível',
          description:
            'Os produtos no seu carrinho possuem condições de pagamento diferentes. Alinhe as condições de pagamento antes de continuar.',
          show: true,
          hiddenLeftButton: true,
          handleNextBtn: handleCloseValidationModal,
          handleNextBtnTitle: 'Continuar'
        });
      }
      return;
    }

    return handleToCheckout();
  };

  const handleToCheckout = async () => {
    try {
      setCartLoading(true);
      setAllItemsFailed(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const multipleSameProducts = hasDuplicates(cartItems);

      const productsDuplicateEqual = cartItems.reduce((acc, item) => {
        const itemAlreadyExist = acc.find((accItem) => {
          const accAtt = accItem.extension_attributes;
          const itemAtt = item.extension_attributes;

          const sameDate = moment(accAtt.expected_date_gerdau).isSame(
            itemAtt.expected_date_gerdau
          );

          return (
            accItem.sku === item.sku &&
            accAtt.measure_unity_gerdau === itemAtt.measure_unity_gerdau &&
            sameDate &&
            accAtt.destination_gerdau === itemAtt.destination_gerdau &&
            accAtt.payment_type_gerdau === itemAtt.payment_type_gerdau &&
            accAtt.tax_benefit_gerdau === itemAtt.tax_benefit_gerdau
          );
        });

        if (itemAlreadyExist) {
          const newAcc = acc.filter(
            (oldAcc) =>
              !(
                oldAcc.sku === itemAlreadyExist.sku &&
                oldAcc.extension_attributes.measure_unity_gerdau ===
                  itemAlreadyExist.extension_attributes.measure_unity_gerdau &&
                oldAcc.extension_attributes.expected_date_gerdau ===
                  itemAlreadyExist.extension_attributes.expected_date_gerdau &&
                oldAcc.extension_attributes.destination_gerdau ===
                  itemAlreadyExist.extension_attributes.destination_gerdau &&
                oldAcc.extension_attributes.payment_type_gerdau ===
                  itemAlreadyExist.extension_attributes.payment_type_gerdau &&
                oldAcc.extension_attributes.tax_benefit_gerdau ===
                  itemAlreadyExist.extension_attributes.tax_benefit_gerdau
              )
          );
          return [
            ...newAcc,
            {
              ...itemAlreadyExist,
              qty: itemAlreadyExist.qty + item.qty
            }
          ];
        }

        return [...acc, item];
      }, [] as NewCartItem[]);

      const convertItems = productsDuplicateEqual
        .filter(
          (prd) =>
            Object.values(prd.cartOptions).length > 0 &&
            prd.extension_attributes &&
            prd.extension_attributes.destination_gerdau
        )
        .map((item) => {
          const extensionAtt = item.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = item.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = item.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const referenceValue = cartItems[0]?.price?.paymentTerms || '';
          const allEqual = cartItems.every(
            (item) => item?.price?.paymentTerms === referenceValue
          );
          const commomPaymentTerms = allEqual
            ? referenceValue
            : cartItems[0]?.price?.paymentTerms || '';

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && item.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * item.qty * 1000
                ) / 1000;

          if (multipleSameProducts) {
            // need delete item_id to create new items
            delete item.item_id;
          }

          return {
            ...item,
            qty: minimunOrderQty,
            ...(newCartEndpoint && {
              extension_attributes: {
                ...item.extension_attributes,
                payment_type_gerdau: commomPaymentTerms,
                expected_date_gerdau: item?.extension_attributes
                  ?.expected_date_gerdau
                  ? moment(
                      item.extension_attributes.expected_date_gerdau
                    ).format('YYYY-MM-DD')
                  : ''
              }
            })
          };
        });

      const convertDirtyItems = dirtyCartItems
        .filter(
          (prd) =>
            Object.values(prd.cartOptions).length > 0 &&
            prd.extension_attributes &&
            prd.extension_attributes.destination_gerdau
        )
        .map((item) => {
          const extensionAtt = item.extension_attributes;
          const destination = extensionAtt.destination_gerdau;
          const cartOptions = item.cartOptions[destination];
          const itemUnits = cartOptions.measureUnits;

          const unit = item.extension_attributes
            .measure_unity_gerdau as EMeasureUnit;
          const currentMeasureUnit = itemUnits.find(
            (unity) => unity.measure === unit
          ) as MeasureUnit;

          const referenceValue = cartItems[0]?.price?.paymentTerms || '';
          const allEqual = cartItems.every(
            (item) => item?.price?.paymentTerms === referenceValue
          );
          const commomPaymentTerms = allEqual
            ? referenceValue
            : cartItems[0]?.price?.paymentTerms || '';

          const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
          const minimunOrderQty =
            measureUnitminQty !== 1 && item.qty === 1
              ? Math.round(
                  Math.ceil(
                    measureUnitminQty / Number(currentMeasureUnit.multiple)
                  ) *
                    Number(currentMeasureUnit.multiple) *
                    1000
                ) / 1000
              : Math.round(
                  Number(currentMeasureUnit.multiple) * item.qty * 1000
                ) / 1000;

          if (multipleSameProducts) {
            // need delete item_id to create new items
            delete item.item_id;
          }

          return {
            ...item,
            qty: minimunOrderQty,
            ...(newCartEndpoint && {
              extension_attributes: {
                ...item.extension_attributes,
                payment_type_gerdau: commomPaymentTerms,
                expected_date_gerdau: item?.extension_attributes
                  ?.expected_date_gerdau
                  ? moment(
                      item.extension_attributes.expected_date_gerdau
                    ).format('YYYY-MM-DD')
                  : ''
              }
            })
          };
        });

      const noHasChange =
        !multipleSameProducts && isEqual(convertItems, convertDirtyItems);

      let dataBulk;

      if (!noHasChange) {
        dataBulk = await addBulkGetCart({
          items: convertItems,
          withoutMimimum: true,
          minimumExisting: minimunOrder,
          ...(multipleSameProducts && {
            itemsToDelete: cartItems.map((item) => ({
              item_id: item.item_id ?? 0
            }))
          }),
          complete: true
        });

        if (
          (dataBulk && dataBulk.cart && dataBulk.cart.hasError) ||
          dataBulk.delete !== true ||
          typeof dataBulk.delete === 'object' ||
          !dataBulk
        ) {
          throw new Error();
        }

        if (
          dataBulk &&
          dataBulk?.cart?.items &&
          Array.isArray(dataBulk.cart.items) &&
          dataBulk.cart.items.length > 0
        ) {
          const itemsWithInformation = await getProductsInformation({
            items: dataBulk.cart.items,
            withInformation: cartItems
          });
          const slicedItems = sliceItems({
            cartItems: itemsWithInformation,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(itemsWithInformation);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);
        }
      }

      const itemsToValidate =
        dataBulk?.cart?.items && dataBulk.cart.items.length > 0
          ? dataBulk.cart.items
          : cartItems;

      const hasErrors = applyBulkErrorState(
        itemsToValidate,
        dataBulk?.errors ?? []
      );
      if (hasErrors) {
        setCartLoading(false);
        return;
      }

      //START FUNCTION TO GO CHECKOUT
      const checkoutServiceMvp = new CheckoutServiceMvp();

      const updatedAt = new Date(
        dataBulk?.cart?.items[0]?.extension_attributes?.quote_updated_at ||
          cartItems[0].extension_attributes?.quote_updated_at ||
          new Date()
      )
        .toISOString()
        .replace('T', ' ')
        .substring(0, 19);

      if ((dataBulk as any)?.complete || noHasChange) {
        if (!shoppingCart.emitterId || !shoppingCart.receiverId) {
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message: 'Serviço indisponível temporariamente, tente novamente',
              durationInMS: 5000
            })
          );
          setCartLoading(false);
          return;
        }

        const param: IDataOrderSimulate = {
          cartNumber: cartId,
          createdAt: updatedAt,
          updatedAt: updatedAt,
          purchaseNumber: '', // preenchido pelo usuário na outra tela
          shipTo: shoppingCart.receiverId,
          soldTo: shoppingCart.emitterId,
          items: [
            ...(dataBulk?.cart?.items && dataBulk?.cart?.items?.length > 0
              ? dataBulk.cart.items
              : cartItems)
          ]
            .filter(
              (prd) =>
                Object.values(prd.cartOptions).length > 0 &&
                prd.extension_attributes &&
                prd.extension_attributes.destination_gerdau
            )
            .map((product) => {
              const extensionAtt = product.extension_attributes;
              const destination = extensionAtt.destination_gerdau;
              const cartOptions = product.cartOptions[destination];
              const itemUnits = cartOptions.measureUnits;
              const unit = product.extension_attributes
                .measure_unity_gerdau as EMeasureUnit;
              const currentMeasureUnit = itemUnits.find(
                (unity) => unity.measure === unit
              ) as MeasureUnit;

              const measureUnitminQty = Number(
                currentMeasureUnit.minimumQuantity
              );
              const minimunOrderQty =
                measureUnitminQty !== 1 && product.qty === 1
                  ? Math.round(
                      Math.ceil(
                        measureUnitminQty / Number(currentMeasureUnit.multiple)
                      ) *
                        Number(currentMeasureUnit.multiple) *
                        1000
                    ) / 1000
                  : Math.round(
                      Number(currentMeasureUnit.multiple) * product.qty * 1000
                    ) / 1000;

              const benefit =
                product.extension_attributes.tax_benefit_gerdau ??
                product.price.salesAgreementData.taxBenefit ??
                '';

              return {
                item_id_gerdau: '',
                name: product.name,
                customerPurchaseItem: '',
                material: product.sku,
                unityMeasure: product.extension_attributes.measure_unity_gerdau,
                requiredDate: product.extension_attributes.expected_date_gerdau
                  ? moment(
                      product.extension_attributes.expected_date_gerdau
                    ).format('YYYY-MM-DD')
                  : '',
                quantity: minimunOrderQty.toString(),
                term: product.extension_attributes.payment_type_gerdau,
                benefit: benefit && benefit !== 'nenhum' ? benefit : '',
                plant: Number(
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].plant
                ),
                item_category:
                  cartItems.find((item) => item.sku === product.sku)
                    ?.descricao_gpm ?? '',
                salesGroup:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesGroup,
                salesOffice:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesOffice,
                salesOrg:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].salesOrg,
                distrChannel: Number(
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].distributionChannel
                ),
                activitySector:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].division,
                industrialType:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].destination.code,
                consumptionOrder: '',
                price_agreement_date_gerdau:
                  product.extension_attributes.price_agreement_date_gerdau ??
                  '',
                sku: product.sku,
                paymentTerms:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].paymentTerms,
                measureUnits: product.cartOptions[
                  product.extension_attributes.destination_gerdau
                ].measureUnits.map((measureUnit) => ({
                  convMeasureKg: measureUnit.convMeasureKg,
                  convMeasureTo: measureUnit.convMeasureTo,
                  measure: measureUnit.measure as EMeasureUnit,
                  minimumQuantity: measureUnit.minimumQuantity,
                  multiple: measureUnit.multiple
                })),
                fiscalBenefits:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].fiscalBenefits,
                description:
                  product.cartOptions[
                    product.extension_attributes.destination_gerdau
                  ].destination.description // do industrialType
              };
            })
        };

        const hasItemsWithoutDate = param.items.some(
          (item) => !item.requiredDate
        );

        if (hasItemsWithoutDate) {
          const itemsWithDateErrors = cartItems.map((item) => ({
            ...item,
            errors: {
              ...item.errors,
              date: !item.extension_attributes.expected_date_gerdau
            }
          }));

          const cartItemsSort = [...itemsWithDateErrors].sort((a, b) => {
            const aHasError = Object.values(a.errors as ErrorProps).some(
              (error) => error
            );
            const bHasError = Object.values(b.errors as ErrorProps).some(
              (error) => error
            );
            if (aHasError && !bHasError) return -1;
            if (!aHasError && bHasError) return 1;
            return 0;
          });

          const slicedItems = sliceItems({
            cartItems: cartItemsSort,
            page: 1,
            size: itemsTable.size
          });

          setCartItems(cartItemsSort);
          setDirtyCartItems(cartItemsSort);
          setItemsTable((prev) => ({
            page: 1,
            size: prev.size,
            items: slicedItems
          }));
          setSelectedItems([]);

          if (shoppingCartRef.current) {
            shoppingCartRef.current.scrollIntoView({ behavior: 'smooth' });
          }

          setCartLoading(false);
          return;
        }

        if (!newCartEndpoint) {
          const orderSimulate: ICheckoutResponse | Error =
            await checkoutServiceMvp.generateOrderSimulate(param);
          if (!(orderSimulate instanceof Error)) {
            dispatch(orderSimulateRequestSuccess(orderSimulate));
            updateCheckoutData({
              ...checkoutState,
              data: orderSimulate,
              params: param,
              favoritedSkus: Array.from(favoritedSkus)
            });
            setValidationModalInformation((prev) => ({
              ...prev,
              show: false
            }));
            setGoToCheckout(true);
            history.push(`${EPurchaseRoutes.CHECKOUT}`);
            return;
          }
        } else {
          updateCheckoutData(CHECKOUT_ORDERS_INITIAL_STATE);

          const filteredCartItems = [
            ...(!noHasChange &&
            dataBulk?.cart?.items &&
            dataBulk?.cart?.items?.length > 0
              ? dataBulk.cart.items
              : cartItems)
          ].filter(
            (prd) =>
              Object.values(prd.cartOptions).length > 0 &&
              prd.extension_attributes &&
              prd.extension_attributes.destination_gerdau
          );

          const orderSimulate: ICheckoutResponse = {
            orders: [
              {
                order:
                  filteredCartItems?.map((product, index) => {
                    const extensionAtt = product.extension_attributes;
                    const destination = extensionAtt.destination_gerdau;
                    const cartOptions = product.cartOptions[destination];

                    const price = extensionAtt.price_gerdau
                      ? JSON.parse(extensionAtt.price_gerdau)
                      : product.price;

                    // Calculate itemPrice following Product component logic
                    const itemUnits = cartOptions.measureUnits;
                    const unit =
                      extensionAtt.measure_unity_gerdau as EMeasureUnit;
                    const currentMeasureUnit = itemUnits.find(
                      (unity) => unity.measure === unit
                    ) as MeasureUnit;

                    const productDate = extensionAtt.expected_date_gerdau;
                    const salesAgreementData = price.salesAgreementData;
                    const endDate = salesAgreementData.expirationDate
                      ? salesAgreementData.expirationDate.replace('Z', '')
                      : salesAgreementData.expirationDate;

                    const salesAgreementExpirationDate =
                      !moment(productDate).isAfter(endDate);

                    const agreementData = {
                      destination: salesAgreementData.destination,
                      payment: salesAgreementData.paymentTerms,
                      expirationDate: endDate
                    };

                    const hasAgreement = () => {
                      const hasAnyAgreementKeyEmpty = Object.values(
                        agreementData
                      ).some((item) => !item);
                      if (salesAgreementData && !hasAnyAgreementKeyEmpty) {
                        return (
                          salesAgreementData.destination ===
                            extensionAtt.destination_gerdau &&
                          salesAgreementData.paymentTerms ===
                            extensionAtt.payment_type_gerdau &&
                          salesAgreementExpirationDate
                        );
                      }
                      return false;
                    };

                    const priceWithoutMeasure =
                      hasAgreement() && salesAgreementData.salesAgreement
                        ? salesAgreementData.salesAgreement
                        : Number(price.totalPrice ?? price.creditPrice) /
                          normalizeQuantity(price.quantity);

                    const convertPriceMeasure = cartOptions.measureUnits.find(
                      (priceOpt) => priceOpt.measure === price.um
                    );

                    const priceKg =
                      priceWithoutMeasure /
                      parseNumber(convertPriceMeasure?.convMeasureKg ?? '1');

                    const measureUnitminQty = Number(
                      currentMeasureUnit.minimumQuantity
                    );
                    const minimunOrderQty =
                      measureUnitminQty !== 1 && product.qty === 1
                        ? Math.ceil(
                            measureUnitminQty /
                              Number(currentMeasureUnit.multiple)
                          ) * Number(currentMeasureUnit.multiple)
                        : Number(currentMeasureUnit.multiple) * product.qty;

                    const multipleWithKG =
                      parseNumber(currentMeasureUnit.convMeasureKg ?? '1') *
                      minimunOrderQty;

                    const itemPrice = priceKg * multipleWithKG;

                    return {
                      item_id: product.item_id,
                      sku: product.sku,
                      qty:
                        param.items[index]?.quantity || product.qty.toString(),
                      quoteId: cartId,
                      extension_attributes: {
                        item_id_gerdau: String((index + 1) * 10).padStart(
                          2,
                          '0'
                        ),
                        measure_unity_gerdau: extensionAtt.measure_unity_gerdau,
                        expected_date_gerdau: extensionAtt.expected_date_gerdau
                          ? moment(extensionAtt.expected_date_gerdau).format(
                              'YYYY-MM-DD'
                            )
                          : '',
                        destination_gerdau: extensionAtt.destination_gerdau,
                        payment_type_gerdau: extensionAtt.payment_type_gerdau,
                        payment_terms_description_gerdau:
                          cartOptions.paymentTerms.find(
                            (pt) => pt.term === extensionAtt.payment_type_gerdau
                          )?.description || '',
                        tax_benefit_gerdau:
                          extensionAtt.tax_benefit_gerdau || '',
                        has_agreed_price_gerdau: hasAgreement(),
                        price_agreement_date_gerdau:
                          extensionAtt.price_agreement_date_gerdau || '',
                        sap_fields: {
                          o_header_sap: {
                            shipto: shoppingCart.receiverId,
                            soldto: shoppingCart.emitterId,
                            purch_no_c:
                              (Array.isArray(
                                extensionAtt.sap_fields.o_header_sap
                              )
                                ? extensionAtt.sap_fields.o_header_sap[0]
                                    ?.purch_no_c
                                : extensionAtt.sap_fields.o_header_sap
                                    .purch_no_c) || '',
                            sales_org: cartOptions.salesOrg,
                            distr_chan: cartOptions.distributionChannel,
                            division: cartOptions.division,
                            sales_grp: cartOptions.salesGroup,
                            sales_off: cartOptions.salesOffice
                          },
                          o_item_sap: {
                            material: product.sku,
                            disp_date: 'loading',
                            beneficio:
                              extensionAtt.tax_benefit_gerdau &&
                              extensionAtt.tax_benefit_gerdau !== 'nenhum'
                                ? extensionAtt.tax_benefit_gerdau
                                : '',
                            beneficiocode: '',
                            plant: cartOptions.plant
                          },
                          o_price_sap: {
                            price_with_discount_total: itemPrice.toFixed(2)
                          }
                        }
                      }
                    };
                  }) || []
              }
            ],
            o_price_sap_totals: {
              price_with_discount_total: getTotal(cartItems),
              price_net: '',
              icms_st: '',
              icms: '',
              ipi: '',
              fcp: '',
              pis: '',
              cofins: '',
              difal: ''
            },
            cartId: cartId,
            cartCreatedIn: updatedAt,
            cartUpdatedIn: updatedAt
          };

          // Verify dispathching three times is necessary toCheckout
          updateCheckoutData({
            ...checkoutState,
            data: orderSimulate,
            params: param,
            favoritedSkus: Array.from(favoritedSkus)
          });

          setValidationModalInformation((prev) => ({
            ...prev,
            show: false
          }));

          setGoToCheckout(true);
          history.push(`${EPurchaseRoutes.CHECKOUT}`);
          return;
        }
      }

      //END FUNCTION TO GO CHECKOUT
      setValidationModalInformation((prev) => ({
        ...prev,
        show: false
      }));

      throw new Error();
    } catch (error) {
      console.error(error);
      setAllItemsFailed(false);
      handleServiceUnavailable({ noClean: true });
    } finally {
      setCartLoading(false);
    }
  };

  if ((!loading && itemsTable.items.length === 0) || !cartId) {
    return (
      <MessageChooseItem message="Seu carrinho de produtos está vazio.">
        <ValidationModal
          {...validationModalInformation}
          loading={loading}
          handleCloseValidationModal={handleCloseValidationModal}
        />
        <Button
          name="addProducts"
          onClick={() => history.push(ERoutes.PURCHASE)}
        >
          Adicionar produtos
        </Button>
      </MessageChooseItem>
    );
  }

  const handlePrevious = () => {
    if (maxItemsV) {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: 'Seu carrinho atingiu o limite de 30 produtos.'
        })
      );
      return dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Seu carrinho atingiu o limite de 30 produtos.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    }

    if (state?.isRepeatOrder) {
      return history.push(
        `${ERoutes.PURCHASE}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`
      );
    }

    setAllItemsFailed(false);
    const menuLinksCache = localStorage.getItem(
      ELocalStorageKey.LONG_STEEL_MENU_NAVEGATION
    );
    const convertedLinks = JSON.parse(
      menuLinksCache ?? ''
    ) as unknown as BreadcrumbItemProps[];

    const link = convertedLinks.slice(
      convertedLinks.length - 2,
      convertedLinks.length - 1
    )[0];

    if (link?.url) {
      if (link.url === 'product') {
        return history.push(`${ERoutes.PURCHASE}/long-steel/commerce/catalog`);
      } else if (link.url === 'order-detail') {
        return history.push(
          `${ERoutes.PURCHASE}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`
        );
      } else {
        return history.push(
          `${ERoutes.PURCHASE}/long-steel/commerce/${link.url}`
        );
      }
    }

    return history.push(ERoutes.PURCHASE);
  };

  const showLoadNewPriceButton = dirtyCartItems.filter((item) => {
    const cartItemCheck = cartItems.find(
      (cart) => cart.item_id === item.item_id
    );
    const itemAtt = item.extension_attributes;
    const cartItemAtt = cartItemCheck?.extension_attributes;

    const productDate = cartItemAtt?.expected_date_gerdau;
    const price = item.extension_attributes.price_gerdau
      ? JSON.parse(item.extension_attributes.price_gerdau)
      : item.price;
    const salesAgreementData = price.salesAgreementData;
    const endDate = salesAgreementData.expirationDate
      ? salesAgreementData.expirationDate.replace('Z', '')
      : salesAgreementData.expirationDate;

    const diff =
      itemAtt.destination_gerdau !== cartItemAtt?.destination_gerdau ||
      itemAtt.payment_type_gerdau !== cartItemAtt?.payment_type_gerdau ||
      itemAtt.tax_benefit_gerdau !== cartItemAtt?.tax_benefit_gerdau ||
      (salesAgreementData && !!endDate && moment(productDate).isAfter(endDate));

    if (diff) {
      return item;
    }
  });

  const hasSomeAgreement = (inptValue: string | DateType) => {
    const hasAgreement = cartItems.reduce(
      (acc, item) => {
        const agreementData = item.price.salesAgreementData;
        const extensionAttributes = item?.extension_attributes;

        const itemAgreement =
          agreementData?.paymentTerms ===
          extensionAttributes?.payment_type_gerdau;

        if (!itemAgreement) {
          return acc;
        }

        //Date
        const endDate = agreementData.expirationDate
          ? agreementData.expirationDate.replace('Z', '')
          : agreementData.expirationDate;

        const selectedDate = moment(inptValue);
        const hasDateAgreement =
          endDate !== '' &&
          !moment(inptValue).isSame(extensionAttributes.expected_date_gerdau) &&
          selectedDate.isAfter(endDate);

        //Destination
        const hasDestination = agreementData.destination !== inptValue;

        //Payment
        const hasPayment = agreementData.paymentTerms !== inptValue;

        //TaxBenefits
        const hasTaxBenefits =
          extensionAttributes.tax_benefit_gerdau !== inptValue;

        return {
          date: hasDateAgreement,
          destination: hasDestination,
          payment: hasPayment,
          fiscalBenefit: hasTaxBenefits
        };
      },
      {
        date: false,
        destination: false,
        payment: false,
        fiscalBenefit: false
      }
    );

    return hasAgreement;
  };

  return (
    <>
      <ShoppingCartContainer
        hasCrossSell={
          ((!crossSellState.items.every(
            (product) =>
              product.price?.creditPrice === '' ||
              !product.price?.creditPrice ||
              Number(product.price?.creditPrice) <= 0
          ) &&
            crossSellState.items.length > 0) ||
            crossSellState.loading) &&
          !loading
        }
      >
        <ValidationModal
          {...validationModalInformation}
          loading={loading}
          handleCloseValidationModal={handleCloseValidationModal}
        />

        <ChangePaymentMethodModal
          {...changePaymentMethodInformation}
          isBlockedCustomer={!!isBlockedCustomer}
          allItems={cartItems}
          loading={loading}
          hasSomeAgreement={hasSomeAgreement}
          handleCloseValidationModal={() =>
            setChangePaymentMethodInformation((prev) => ({
              ...prev,
              show: false
            }))
          }
          handleNextBtn={handleUpdateAllItems}
        />

        {shouldShowOnlyLoading && (
          <LoadingModal
            enabled
            title="Preparando o seu pedido"
            message="Isso pode levar alguns minutos, dependendo da quantidade de itens no seu pedido."
          />
        )}

        <CartHeader
          productsQuantity={cartItems.length}
          graphs={generateGraph(cartItems)}
          loading={loading}
          showMinimumAlert={!loading && validations.minimum}
          showMaximumAlert={!loading && (validations.maximum || maxItemsV)}
          showPartialAlert={!loading && validations.partial}
        />
        {(customer || receiverCartInfo) && (
          <CartBpInfo>
            {customer && (
              <SelectedBpCard
                name={customer.name}
                address={customer.locality.address}
                city={customer.locality.city}
                state={customer.locality.state}
                zipCode={customer.locality.zipCode}
              />
            )}
            {receiverCartInfo && (
              <SelectedBpCard
                name={receiverCartInfo.name}
                address={receiverCartInfo.address}
                city={receiverCartInfo.city}
                state={receiverCartInfo.state}
                zipCode={receiverCartInfo.postalCode}
              />
            )}
          </CartBpInfo>
        )}
        <CartProductTable
          shoppingCartRef={shoppingCartRef}
          selectedItems={selectedItems}
          allItems={cartItems}
          dirtyCartItems={dirtyCartItems}
          itemsTable={itemsTable}
          handleUpdateItem={handleUpdateItem}
          handleDuplicateItem={handleDuplicateItem}
          handleDeleteItem={handleDeleteItem}
          handleDeleteAllItems={handleDeleteAllItems}
          handleUpdateAllItems={handleUpdateAllItems}
          handleUpdateSingleItem={handleUpdateSingleItem}
          handleSelectAll={handleSelectAll}
          handleSelectProduct={handleSelectProduct}
          isBlockedCustomer={!!isBlockedCustomer}
          loading={loading}
          allItemsFailed={allItemsFailed}
          favoritedSkus={favoritedSkus}
          handleWishlistToggle={handleCartWishlistToggle}
          key={JSON.stringify(itemsTable.items.map((dirty) => dirty.item_id))}
        />
        <CartPagination
          key={JSON.stringify(dirtyCartItems)}
          handleChange={handleChangePagination}
          loading={loading}
          total={cartItems.length}
        />
      </ShoppingCartContainer>
      <SummaryBottom
        loading={loading}
        hasProducts={cartItems.length !== 0}
        totalPrice={getTotal(cartItems)}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        showLoadNewPriceButton={showLoadNewPriceButton.length > 0}
        onLoadNewPrice={handleUpdateAllItems}
        goToCheckout={goToCheckout}
        isBlockedCustomer={!!isBlockedCustomer}
        cartItems={cartItems}
        allItemsFailed={allItemsFailed}
        handleChangePaymentMethod={() =>
          setChangePaymentMethodInformation({
            title: 'Alterar a condição de pagamento',
            description:
              'A alteração da condição de pagamento afetará os preços e acordos de todos os produtos do seu carrinho. Você tem como opções somente condições que atendem todos esses itens. Selecione uma opção:',
            show: true,
            handleNextBtn: handleCloseValidationModal,
            handleNextBtnTitle: 'Alterar',
            hasCloseBtn: true
          })
        }
      />
    </>
  );
};
export default ShoppingCartMvp;
